import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  WindowScroller,
} from 'react-virtualized';
import _ from 'lodash';
import moment from 'moment';

import i18n from '../../config/i18n';
import MedicalRecordPreview from './MedicalRecordPreview';
import {
  LoadingMessageItem,
  SystemMessageItem,
  TimeMessageItem,
  UserMessageItem,
  UserTag,
  MedicalRecordTag,
} from '../../modules/messaging/components/ChannelDetail/Message';
import {
  REGEX_MEDICAL_RECORD_TAG,
  REGEX_USER_TAG,
} from '../../modules/messaging/costants';

// Custom hook
const usePrevious = (value) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const styles = {
  root: {
    flex: 1,
  },
};

// Scroll Bar Positions
const MIDDLE = 'middle';
const BOTTOM = 'bottom';

const COMBINE_MESSAGE_INTERVAL = 5; // minutes


const ChannelDetailContent = ({
  channelMembers,
  className,
  currentChannelId,
  currentUserId,
  membersByUserId,
  messages: messagesProp,
  onFetchPrevChannelMessages,
  medicalRecordDetail,
  medicalRecords,
  resizeTrigger,
  onClickMedicalRecordTag,
}) => {
  const [lastScrollLeftItemCnt, setLastScrollLeftItemCnt] = useState(0);
  const [scrollToIndex, setScrollToIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isFetchingPreviousMsgs, setIsFetchingPreviousMsgs] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const cacheRef = useRef(new CellMeasurerCache({
    defaultHeight: 50,
    fixedWidth: true,
  }));
  const listRef = useRef();
  const nvRef = useRef();
  const hasLoadingIndicator = useRef(false);
  const scrollBarPosRef = useRef(BOTTOM);

  const tagParsers = useMemo(() => [
    {
      pattern: REGEX_USER_TAG,
      render: (text, key) => (
        <UserTag
          key={key}
          text={text}
          membersByUserId={membersByUserId}
        />
      ),
    },
    {
      pattern: REGEX_MEDICAL_RECORD_TAG,
      render: (text, key) => (
        <MedicalRecordTag
          key={key}
          text={text}
          medicalRecords={medicalRecords}
          onClick={
            async (medicalRecordId) => {
              setShowPreview(true);
              await onClickMedicalRecordTag(medicalRecordId);
            }
          }
        />
      ),
    },
  ], [membersByUserId, medicalRecords, onClickMedicalRecordTag]);

  const prevProps = usePrevious({
    currentChannelId,
    messages: messagesProp,
    membersByUserId,
  });
  const currentMsgIndicesString = _.join(_.map(membersByUserId, member => member.currentMsgIdx), ' ');

  const handleCopyNPaste = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    const nv = nvRef.current;
    nv.addEventListener('contextmenu', handleCopyNPaste);
    nv.addEventListener('keydown', handleCopyNPaste);
    return () => {
      nv.removeEventListener('contextmenu', handleCopyNPaste);
      nv.removeEventListener('keydown', handleCopyNPaste);
    };
  }, []);

  // NOTE:
  // check if these new msg props is caused by infinite scrolling or create new message.
  const checkIfNeededPrependMsgs = () => {
    const {
      currentChannelId: prevChannelId,
      messages: prevMsgs,
    } = prevProps;

    if (_.isEmpty(prevMsgs) || _.isEmpty(messagesProp)) return false;

    const prevMsgsCnt = prevMsgs.length;
    const nextMsgsCnt = messagesProp.length;
    if (prevChannelId === currentChannelId && prevMsgsCnt < nextMsgsCnt) {
      if (messagesProp[0].seq < prevMsgs[0].seq) return true;
      if (messagesProp[nextMsgsCnt - 1].seq > prevMsgs[prevMsgsCnt - 1].seq) return false;
    }
    return false;
  };

  // NOTE:
  // If oldest seq === startMsgIdx, the member doesn't need to fetch previous message
  // Additionally, if oldSeqNumber is 1, neither does this case obviously.
  const shouldShowLoadIndicator = () => {
    if (_.isEmpty(channelMembers) || _.isEmpty(messagesProp)) return false;
    const oldSeqNumber = messagesProp[0].seq;
    return channelMembers[currentUserId].startMsgIdx < oldSeqNumber && oldSeqNumber !== 1;
  };

  useEffect(() => {
    const newMessages = [];

    messagesProp.forEach((message, index) => {
      const { createdAt } = message;

      if (index > 0) {
        const interval = moment(createdAt).diff(moment(messagesProp[index - 1].createdAt), 'minutes');
        const isLongerThanInterval = interval > COMBINE_MESSAGE_INTERVAL;

        if (isLongerThanInterval) {
          newMessages.push({
            creatorId: -2,
            createdAt,
          });
        }

        if (message.creatorId === messagesProp[index - 1].creatorId) {
          newMessages.push({ ...message, simple: !isLongerThanInterval });
          return;
        }
      } else {
        newMessages.push({
          creatorId: -2,
          createdAt,
        });
      }

      newMessages.push({ ...message, simple: false });
    });

    hasLoadingIndicator.current = shouldShowLoadIndicator();

    if (hasLoadingIndicator.current) {
      newMessages.unshift({
        creatorId: -1,
        text: i18n.t('loadPrevMsg'),
      });
    }

    if (!checkIfNeededPrependMsgs()) {
      setMessages(newMessages);
      setScrollToIndex(newMessages.length - 1);
      setLastScrollLeftItemCnt(0);
    } else {
      // NOTE: clean the cache because prepend msgs will populate old message
      // component which would reuse older row height so we need to recompute
      // row height for new msgs prepend from the top.
      cacheRef.current.clearAll();
      listRef.current.recomputeRowHeights();
      setMessages(newMessages);
      setScrollToIndex(newMessages.length - lastScrollLeftItemCnt);
      setIsFetchingPreviousMsgs(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesProp]);

  useEffect(() => {
    cacheRef.current.clearAll();
    listRef.current.forceUpdateGrid();
  }, [currentMsgIndicesString]);

  useEffect(() => {
    // NOTE: clean cache because user switch the channel
    cacheRef.current.clearAll();
    listRef.current.recomputeRowHeights();
  }, [currentChannelId]);

  useEffect(() => {
    // NOTE:
    // if channelId is the same and member info updated
    // we need to refresh react-virtualized list component
    if (prevProps.currentChannelId === currentChannelId
      && prevProps.membersByUserId !== membersByUserId) {
      listRef.current.forceUpdateGrid();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChannelId, membersByUserId]);

  /**
   * When opening the channel setting, the width of the message window will change
   * and the height of message items may change, too. But the List will not recompute
   * row height automatically. So, we force the list to recompute row height.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      cacheRef.current.clearAll();
      listRef.current.forceUpdateGrid();
      if (scrollBarPosRef.current === BOTTOM) { setScrollToIndex(messages.length - 1); }
    }, 1);
    return () => clearTimeout(timer);
  }, [messages.length, resizeTrigger]);

  const redirectToMedicalRecord = () => {
    window.open(`/medical-records/${medicalRecordDetail.id}`, '_blank');
  };

  const handleScroll = ({ clientHeight, scrollHeight, scrollTop }) => {
    scrollBarPosRef.current = clientHeight + scrollTop >= scrollHeight ? BOTTOM : MIDDLE;

    // NOTE: if there no indicator, we don't need to load more
    if (!hasLoadingIndicator.current) return;

    // NOTE:
    // This is the tricky part. To prevent first load from loading more previous
    // messages which would happen automatically in the case of scrolling upwardly
    // to load more, add this condition to avoid it
    if (scrollTop === 0 && clientHeight === 0 && scrollHeight === 0) return;

    // NOTE:
    // To make scrolling smooth, we record the last scroll up position and set
    // the right position after loading previous message finish
    if (scrollTop === 0 && !isFetchingPreviousMsgs) {
      setIsFetchingPreviousMsgs(true);
      setLastScrollLeftItemCnt(messages.length);
      onFetchPrevChannelMessages();
    }
  };

  const handleResize = () => {
    cacheRef.current.clearAll();
  };

  // To prevent the scroll bar from automatically moving to the index
  // every time the list rerender, we assign -1 to the scrollToIndex if
  // the list has already moved to the index.
  const handleRowRendered = ({ startIndex, stopIndex }) => {
    if (scrollToIndex >= startIndex && scrollToIndex <= stopIndex) {
      setScrollToIndex(-1);
    }
  };

  // NOTE: render different kinds of message component
  // 1. system msg like invitation, updating title
  // 2. user generated text msg (include image message)
  // 3. realtime update seen meta information (using forceUpdate)
  const rowRenderer = ({ key, index, style, parent }) => (
    <CellMeasurer
      cache={cacheRef.current}
      columnIndex={0}
      key={key}
      parent={parent}
      rowIndex={index}
    >
      {({ measure }) => {
        switch (messages[index].creatorId) {
          case -2: {
            return (
              <TimeMessageItem
                key={key}
                style={style}
                time={messages[index].createdAt}
              />
            );
          }
          case -1: {
            return <LoadingMessageItem key={key} style={style} />;
          }
          case 0: {
            return (
              <SystemMessageItem
                key={key}
                message={messages[index]}
                style={style}
              />
            );
          }
          default: {
            return (
              <UserMessageItem
                currentUserId={currentUserId}
                key={key}
                measure={() => {
                  measure();
                  if (scrollBarPosRef.current === BOTTOM) {
                    setScrollToIndex(messages.length - 1);
                  }
                }}
                membersByUserId={membersByUserId}
                message={messages[index]}
                style={style}
                tagParsers={tagParsers}
              />
            );
          }
        }
      }}
    </CellMeasurer>
  );

  return (
    <div
      className={className}
      ref={nvRef}
      style={styles.root}
    >
      <WindowScroller onResize={handleResize} >
        {() => (
          <AutoSizer>
            {({ height, width }) => (
              <List
                deferredMeasurementCache={cacheRef.current}
                ref={listRef}
                width={width}
                height={height}
                rowHeight={cacheRef.current.rowHeight}
                rowCount={messages.length}
                rowRenderer={rowRenderer}
                scrollToIndex={scrollToIndex}
                scrollToAlignment="start"
                onRowsRendered={handleRowRendered}
                onScroll={handleScroll}
                overscanRowCount={15}
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>
      <MedicalRecordPreview
        cancelText={i18n.t('close')}
        okText={i18n.t('more')}
        onCancel={() => setShowPreview(false)}
        onClose={() => setShowPreview(false)}
        onOk={redirectToMedicalRecord}
        medicalRecordDetail={medicalRecordDetail}
        visible={showPreview}
      />
    </div>
  );
};

ChannelDetailContent.propTypes = {
  channelMembers: PropTypes.object,
  className: PropTypes.string,
  currentChannelId: PropTypes.number.isRequired,
  currentUserId: PropTypes.number.isRequired,
  membersByUserId: PropTypes.object,
  messages: PropTypes.array,
  onFetchPrevChannelMessages: PropTypes.func.isRequired,
  onGetPhotoDownloadURL: PropTypes.func.isRequired,
};

ChannelDetailContent.defaultProps = {
  channelMembers: {},
  className: '',
  membersByUserId: {},
  messages: [],
};


export default ChannelDetailContent;



// WEBPACK FOOTER //
// ./src/components/messaging/ChannelDetailContent.js